/**
 * Client info helpers used by the auth layer.
 *
 * - getDeviceType()  : synchronous. Parses navigator.userAgent into a short
 *                      label like "Desktop/Chrome", "Mobile/Android",
 *                      "Tablet/iOS".
 * - getClientIp()    : async. Fetches the public IPv4/IPv6 via ipify with a
 *                      4-second timeout, then caches for the browser session
 *                      so we don't hammer the third-party service.
 *
 * Both fall back to "" on failure. Login must never block on a slow IP
 * lookup — the server can still complete auth without it.
 */

const IP_CACHE_KEY = 'chanakya.client.ip';
const IP_ENDPOINT = 'https://api.ipify.org?format=json';

export function getDeviceType() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';

  // Rough category. Android tablets do NOT include "Mobile" in the UA string.
  const isTablet = /iPad|Tablet|(Android(?!.*Mobile))/i.test(ua);
  const isMobile =
    !isTablet && /Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua);
  const category = isTablet ? 'Tablet' : isMobile ? 'Mobile' : 'Desktop';

  // Desktop → identify by browser. Mobile/tablet → identify by OS
  // (matches the user's example format "Desktop/Chrome" vs "Mobile/Android").
  if (category === 'Desktop') {
    let browser = 'Browser';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/OPR\//.test(ua) || /Opera\//.test(ua)) browser = 'Opera';
    else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua)) browser = 'Safari';
    return `Desktop/${browser}`;
  }

  let os = 'Unknown';
  if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
  else if (/Windows Phone/i.test(ua)) os = 'Windows';
  return `${category}/${os}`;
}

export async function getClientIp() {
  if (typeof window === 'undefined') return '';
  try {
    const cached = sessionStorage.getItem(IP_CACHE_KEY);
    if (cached) return cached;
  } catch {
    /* sessionStorage may throw in private mode; ignore */
  }

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    const res = await fetch(IP_ENDPOINT, {
      signal: controller.signal,
      cache: 'no-store',
    });
    clearTimeout(timer);
    if (!res.ok) return '';
    const data = await res.json();
    const ip = String(data?.ip || '');
    if (ip) {
      try {
        sessionStorage.setItem(IP_CACHE_KEY, ip);
      } catch {
        /* ignore */
      }
    }
    return ip;
  } catch {
    return '';
  }
}

/** Kick off an IP prefetch — safe to fire from a component mount. */
export function warmupClientIp() {
  getClientIp().catch(() => {});
}
